import React, { useState, useEffect } from 'react';
import { Save, Settings, ChevronDown, ChevronRight, RotateCcw } from 'lucide-react';
import { ROLE_PERMISSIONS } from '../../constants/permissions';
import { Button } from '../../../../components/ui/button';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Card, CardContent } from '../../../../components/ui/card';
import { useUserAccessRedux } from '../../hooks/useUserAccessRedux';
import './UserAccess.css';
import { UserPermissionsAfterLoginIndex } from '../UserPermissionsAfterLogin/Index';
import { RoleBasedAccessControl } from '../RoleBasedAccessControl/Index';
import { PermissionExample } from '../PermissionExample/Index';

export const UserAccessIndex: React.FC = () => {
    const {
        rolePermissions,
        loading,
        saving,
        restoring,
        error,
        lastSaved,
        lastRestored,
        fetchUserAccess,
        updatePermission,
        savePermissions,
        restorePermissions,
        initializePermissions,
        getPermissionState
    } = useUserAccessRedux();

    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
        super_admin: true,
        admin: true,
        manager: true,
        user: true,
        viewer: true
    });

    // Initialize permissions for all roles
    useEffect(() => {
        if (Object.keys(rolePermissions).length === 0) {
            const initialPermissions = initializePermissions();
            // Initialize with default permissions if no data from Redux
            if (initialPermissions) {
                // This will be handled by Redux saga when we fetch from DB
                fetchUserAccess();
            }
        }
    }, [rolePermissions, initializePermissions, fetchUserAccess]);

    // Handle permission change
    const handlePermissionChange = (role: string, permissionId: string, checked: boolean) => {
        if(role === 'super_admin') return;
        updatePermission(role, permissionId, checked);
    };

    const toggleSection = (role: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [role]: !prev[role]
        }));
    };

    const handleSave = async () => {
        // Pass the entire rolePermissions.data array to save
        if (rolePermissions?.data && Array.isArray(rolePermissions.data)) {
            savePermissions(rolePermissions.data);
        }
    };

    const handleRestore = async () => {
        restorePermissions();
    };

    const getRoleColor = (role: string) => {
        const colors = {
            super_admin: 'from-slate-100 to-slate-200',
            admin: 'from-blue-50 to-blue-100',
            manager: 'from-emerald-50 to-emerald-100',
            user: 'from-amber-50 to-amber-100',
            viewer: 'from-slate-50 to-slate-100'
        };
        return colors[role as keyof typeof colors] || 'from-slate-50 to-slate-100';
    };

    const getRoleIcon = (role: string) => {
        const icons = {
            super_admin: '👑',
            admin: '⚡',
            manager: '👔',
            user: '👤',
            viewer: '👁️'
        };
        return icons[role as keyof typeof icons] || '👤';
    };

    const PermissionCard = ({ role, permissions, title }: { role: string; permissions: any[]; title: string }) => {
        // Safety check to ensure permissions exist
        if (!permissions || !Array.isArray(permissions)) {
            return null;
        }
        
        return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-md">
            <div
                className={`bg-gradient-to-r ${getRoleColor(role)} p-6 cursor-pointer transition-all duration-200 hover:shadow-lg`}
                onClick={() => toggleSection(role)}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <span className="text-2xl">{getRoleIcon(role)}</span>
                        <div>
                            <h3 className="text-slate-800 text-lg font-semibold">{title}</h3>
                            <p className="text-slate-600 text-sm">
                                {permissions?.length || 0} permissions available
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className="bg-white/80 backdrop-blur-sm rounded-full px-3 py-1 border border-white/60 shadow-sm">
                            <span className="text-slate-700 text-xs font-medium">
                                {(() => {
                                    // Safely get the checked count from the new data structure
                                    let checkedCount = 0;
                                    let totalCount = permissions?.length || 0;
                                    
                                    if (rolePermissions?.data && Array.isArray(rolePermissions.data)) {
                                        const roleData = rolePermissions.data.find((r: any) => r.group_name === role);
                                        if (roleData?.permissions && roleData.permissions[role]) {
                                            checkedCount = roleData.permissions[role].filter((p: any) => p.value === true).length;
                                        }
                                    }
                                    
                                    return `${checkedCount} / ${totalCount}`;
                                })()}
                            </span>
                        </div>
                        {expandedSections[role] ? (
                            <ChevronDown className="h-5 w-5 text-slate-600 transition-transform duration-200" />
                        ) : (
                            <ChevronRight className="h-5 w-5 text-slate-600 transition-transform duration-200" />
                        )}
                    </div>
                </div>
            </div>

            {expandedSections[role] && (
                <div className="p-6 bg-slate-50/60">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {permissions?.map((permission) => {
                            // Get the current checked state using the Redux hook function
                            const isChecked = getPermissionState(role, permission.id);
                            return (
                                <div
                                    key={permission.id}
                                    className="permission-item group relative bg-white rounded-lg p-4 border border-gray-200 hover:shadow-sm transition-all duration-200 cursor-pointer"
                                    onClick={() => handlePermissionChange(role, permission.id, !isChecked)}
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className="checkbox-container flex-shrink-0 mt-1">
                                            <div className="relative">
                                                <Checkbox
                                                   disabled={role === 'super_admin'}
                                                    checked={isChecked}
                                                    onCheckedChange={(checked) => handlePermissionChange(role, permission.id, checked as boolean)}
                                                />
                                            </div>
                                        </div>
                                        <div className="permission-content flex-1 min-w-0">
                                            <label className="block text-sm font-medium text-gray-900 cursor-pointer group-hover:text-blue-600 transition-colors duration-200">
                                                {permission.name}
                                            </label>
                                            {permission.description && (
                                                <p className="mt-1 text-xs text-gray-500 line-clamp-2">
                                                    {permission.description}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Hover effect overlay */}
                                    <div className="absolute inset-0 bg-blue-50 opacity-0 group-hover:opacity-100 rounded-lg transition-opacity duration-200 pointer-events-none z-0"></div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    )};

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 p-6">
            <div className="max-w-full mx-auto">
              
                {/* <UserPermissionsAfterLoginIndex /> */}
                {/* Header Section */}
                <div className="mb-8">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                            <div className="flex items-center space-x-4">
                                <div
                                    className="bg-gradient-to-r from-slate-200 to-slate-300 p-3 rounded-xl shadow-sm"
                                >
                                    <Settings className="h-8 w-8 text-slate-700" />
                                </div>
                                <div>
                                    <h1 className="text-lg font-semibold text-gray-900">
                                        User Permissions Management
                                    </h1>
                                    {/* <p className="text-gray-600 text-lg mt-1">
                                        Configure system access levels and role-based permissions
                                    </p> */}
                                     {/* <span className="text-2xl text-gray-600 text-lg mt-1">{getRoleIcon('super_admin')}  Super Admin has all permissions.</span> */}
                                    
                                </div>
                            </div>
                            <div className="flex items-center space-x-3">
                                {/* <Button
                                    variant="outline"
                                    onClick={handleRestore}
                                    disabled={restoring}
                                    className="bg-white hover:bg-gray-50 text-gray-700 border-gray-300 hover:border-gray-400 px-6 py-3 text-base font-medium rounded-xl shadow-sm hover:shadow-md transition-all duration-200"
                                >
                                    <RotateCcw className={`mr-2 h-5 w-5 ${restoring ? 'animate-spin' : ''}`} />
                                    {restoring ? 'Restoring...' : 'Restore from DB'}
                                </Button> */}
                                <Button
                                    loading={saving}
                                    onClick={handleSave}
                                    loadingText='Saving...' 
                                    disabled={saving}
                                    //className="bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white px-8 py-3 text-base font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                                >
                                    <Save className="mr-2 h-5 w-5" />
                                    {saving ? 'Saving...' : 'Save All Permissions'}
                                </Button>
                            </div>
                        </div>
                        
                       
                    </div>
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="mb-6 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <div className="text-center">
                         
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
                            <p className="text-gray-600 mt-2">Loading permissions from database...</p>
                        </div>
                    </div>
                )}

                {/* Permissions Grid */}
                {!loading && (
                    <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <PermissionCard
                            role="super_admin"
                            permissions={ROLE_PERMISSIONS.find((r: any) => r.group_name === 'super_admin')?.permissions?.super_admin || []}
                            title="Super Administrator"
                        />

                        <PermissionCard
                            role="admin"
                            permissions={ROLE_PERMISSIONS.find((r: any) => r.group_name === 'admin')?.permissions?.admin || []}
                            title="Administrator"
                        />

                        <PermissionCard
                            role="manager"
                            permissions={ROLE_PERMISSIONS.find((r: any) => r.group_name === 'manager')?.permissions?.manager || []}
                            title="Manager"
                        />

                        <PermissionCard
                            role="user"
                            permissions={ROLE_PERMISSIONS.find((r: any) => r.group_name === 'user')?.permissions?.user || []}
                            title="Standard User"
                        />

                        <PermissionCard
                            role="viewer"
                            permissions={ROLE_PERMISSIONS.find((r: any) => r.group_name === 'viewer')?.permissions?.viewer || []}
                            title="Viewer"
                        />
                    </div>
                )}

                {/* Summary Footer */}
                {/* <div className="mt-8 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="text-center">
                        <p className="text-gray-600 text-sm">
                            Total permissions configured: <span className="font-semibold text-blue-600">
                                {(() => {
                                    let total = 0;
                                    if (rolePermissions?.data && Array.isArray(rolePermissions.data)) {
                                        rolePermissions.data.forEach((roleData: any) => {
                                            if (roleData?.permissions && typeof roleData.permissions === 'object') {
                                                Object.values(roleData.permissions).forEach((rolePerms: any) => {
                                                    if (Array.isArray(rolePerms)) {
                                                        total += rolePerms.filter((p: any) => p.value === true).length;
                                                    }
                                                });
                                            }
                                        });
                                    }
                                    return total;
                                })()}
                            </span>
                        </p>
                        <p className="text-gray-500 text-xs mt-2">
                            Changes are automatically saved when you click the save button
                        </p>
                    </div>
                </div> */}
            </div>
        </div>
    );
};

export default UserAccessIndex;
